import { Provider } from '@angular/core';
import { Store } from '@ngrx/store';

import { RoleService } from '@ninety/platform/services/services/role.service';
import {
  TeamListStateActions,
  UserTeamsActions,
} from '@ninety/platform/state/app-entities/team-list/team-list-state.actions';
import { EditedTeamSelectors } from '@ninety/platform/state/app-entities/team-list/team-list-state.selectors';
import { selectAllNonCoachActiveUsers } from '@ninety/platform/state/app-entities/users/users-state.selectors';
import { TeamDetailSelectors } from '@ninety/platform/state/composite-selectors/user-team.selectors';
import {
  ASSIGNABLE_USERS_STREAM,
  CAN_EDIT_TEAM_STREAM,
  EDITED_TEAM_STREAM,
  IS_COACH_PREDICATE,
  IsCoachPredicate,
  TEAM_DETAIL_VIEW_CLOSED,
  TEAM_FETCH_IN_PROGRESS_STREAM,
  TEAM_MEMBER_IDS_STREAM,
  TEAM_MEMBERS_STREAM,
  TEAM_RENAMED,
  TeamDetailViewClosedHandler,
  TeamRenamedHandler,
  USER_REMOVED_FROM_TEAM,
  USERS_ADDED_TO_TEAM,
  UserRemovedFromTeamHandler,
  UsersAddedToTeamHandler,
} from '@ninety/ui/core';

/** `RoleService.isCoach` is static and `this`-free; wrapped so the token's shape is asserted here. */
const isCoach: IsCoachPredicate = user => RoleService.isCoach(user);

/**
 * Binds the tokens consumed by `TeamCardComponent` in `@ninety/ui/core` to their platform-backed
 * selectors and actions. The card is a route-level container with no inputs or outputs, so all six of its
 * store reads (`EditedTeamSelectors.selectTeam` / `.selectFetchInProgress`,
 * `TeamDetailSelectors.selectTeamUsers` / `.selectTeamUserIds` / `.selectCanEdit`,
 * `selectAllNonCoachActiveUsers`), all four of its dispatches (`TeamListStateActions.updateOne`,
 * `UserTeamsActions.closeDetailView` / `.addUsersToTeam` / `.removeUserFromTeam`) and its one
 * `RoleService.isCoach` call come through here. Neither `@ninety/platform/state` nor
 * `@ninety/platform/services` has a build target, so neither can be compiled into the public package;
 * this composition-root wiring is the single place that depends on both sides.
 *
 * `EDITED_TEAM_STREAM` is bound to the bare selector — the component keeps the `distinctUntilChanged()`
 * and the `teamNameControl` seeding side effect on its own side of the boundary. (DEV-26530 part 16)
 */
export const TEAM_CARD_PROVIDERS: Provider[] = [
  {
    provide: EDITED_TEAM_STREAM,
    useFactory: (store: Store) => store.select(EditedTeamSelectors.selectTeam),
    deps: [Store],
  },
  {
    provide: TEAM_MEMBERS_STREAM,
    useFactory: (store: Store) => store.select(TeamDetailSelectors.selectTeamUsers),
    deps: [Store],
  },
  {
    provide: TEAM_MEMBER_IDS_STREAM,
    useFactory: (store: Store) => store.select(TeamDetailSelectors.selectTeamUserIds),
    deps: [Store],
  },
  {
    provide: CAN_EDIT_TEAM_STREAM,
    useFactory: (store: Store) => store.select(TeamDetailSelectors.selectCanEdit),
    deps: [Store],
  },
  {
    provide: TEAM_FETCH_IN_PROGRESS_STREAM,
    useFactory: (store: Store) => store.select(EditedTeamSelectors.selectFetchInProgress),
    deps: [Store],
  },
  {
    provide: ASSIGNABLE_USERS_STREAM,
    useFactory: (store: Store) => store.select(selectAllNonCoachActiveUsers),
    deps: [Store],
  },
  {
    provide: TEAM_RENAMED,
    useFactory:
      (store: Store): TeamRenamedHandler =>
      ({ teamId, name }) =>
        store.dispatch(TeamListStateActions.updateOne({ id: teamId, update: { name } })),
    deps: [Store],
  },
  {
    provide: TEAM_DETAIL_VIEW_CLOSED,
    useFactory:
      (store: Store): TeamDetailViewClosedHandler =>
      () =>
        store.dispatch(UserTeamsActions.closeDetailView()),
    deps: [Store],
  },
  {
    provide: USERS_ADDED_TO_TEAM,
    useFactory:
      (store: Store): UsersAddedToTeamHandler =>
      ({ teamId, users }) =>
        store.dispatch(UserTeamsActions.addUsersToTeam({ users, teamId })),
    deps: [Store],
  },
  {
    provide: USER_REMOVED_FROM_TEAM,
    useFactory:
      (store: Store): UserRemovedFromTeamHandler =>
      ({ teamId, userId }) =>
        store.dispatch(UserTeamsActions.removeUserFromTeam({ userId, teamId })),
    deps: [Store],
  },
  { provide: IS_COACH_PREDICATE, useValue: isCoach },
];
